/**
 * workflowConvergence.ts — Phase 3 Surface & Lineage Primitive
 *
 * Centralizes the **operator-visible** convergence between hand-offs and
 * the AI-derived Action Queue, plus deterministic lineage walking for
 * chained hand-offs.
 *
 * IMPORTANT: This module does NOT couple persisted hand-offs to the
 * non-persisted Action Queue at the schema level. It only:
 *   1. Merges them at the surface (one ranked attention list).
 *   2. Dedupes when a hand-off and an AI item describe the *same operational verb*
 *      against the *same job*.
 *   3. Walks `originHandoffId` chains for audit/lineage queries.
 *
 * When Action Queue is promoted to a persisted, owned record type
 * (a future phase), bidirectional `linkedActionId` linkage and completion
 * sync will be added HERE — no other file should need to learn about it.
 */

import type { NavigateFunction } from "react-router-dom";
import type { FollowUpTask, HandoffIntent } from "./opportunityTypes";
import type { Job, Issue } from "./types";
import type { AttentionItem } from "@/components/AttentionItemDetailModal";
import {
  getEffectiveIntent,
  resolveHandoffAction,
  INTENT_LABELS,
  type HandoffActionRunners,
} from "./handoffActions";
import { runAction } from "@/lib/actionRunner";
import type { ActionQueueItem } from "./actionQueue/types";
import { resolveQueueItemAction } from "./actionQueue/resolver";

// ─────────────────────────────────────────────────────────────────────────────
// Merged attention entry — superset shape consumed by WhatNeedsAttentionStrip.
// We intentionally mirror the strip's existing `AttentionEntry` so integration
// is a one-line concat, not a refactor.
// ─────────────────────────────────────────────────────────────────────────────

export type AttentionSourceKind = "handoff" | "ai_action" | "signal";

export interface MergedAttentionEntry {
  id: string;
  title: string;
  context: string;
  severity: "critical" | "warning" | "info";
  /** Where the entry originated — used for the source chip. */
  sourceKind: AttentionSourceKind;
  /** Human label for the source chip (e.g. "Hand-off · Material Confirmation"). */
  sourceLabel: string;
  /** Linked job id when applicable (for dedupe + secondary "Open job" link). */
  jobId?: string;
  /** Optional revenue at risk for ranking. */
  revenueAtRisk?: number;
  /** Primary CTA label. */
  cta: string;
  /** Click handler — already wired to runAction or navigation. */
  onActivate: () => void;
  /** Secondary "Open source" link target — present when source linkage exists. */
  secondaryHref?: string;
  /** Original task reference, when this entry came from a hand-off. */
  handoffTask?: FollowUpTask;
  /**
   * Phase 5: when this entry is a queue item linked back to a hand-off,
   * the originating hand-off id is surfaced for a lightweight UI chip
   * ("← from hand-off"). Convergence is the single source of truth.
   */
  linkedFromHandoffId?: string;
  /**
   * Phase 7.1 — linked records for unified workspace rendering.
   * Pure additions: when present, the workspace can derive rich context
   * (economics, material state, risk) without a second lookup. Optional
   * to preserve backward compatibility with all existing consumers.
   */
  linkedJob?: Job;
  linkedIssue?: Issue;
  linkedQueueItem?: ActionQueueItem;
}

// ─────────────────────────────────────────────────────────────────────────────
// Intent → AI verb dedupe map.
// A hand-off SUPPRESSES an AI item only when both target the same job AND
// the AI item's primary action falls in the matching verb bucket. Anything
// fuzzy/ambiguous → show both. Bias is "operator sees more, not less".
// ─────────────────────────────────────────────────────────────────────────────

type DedupeVerb =
  | "material"
  | "schedule"
  | "permit"
  | "service_issue"
  | "finance"
  | "estimate"
  | "customer_contact";

const INTENT_TO_VERB: Partial<Record<HandoffIntent, DedupeVerb>> = {
  material_confirmation: "material",
  schedule_coordination: "schedule",
  permit_followup: "permit",
  service_resolution: "service_issue",
  finance_followup: "finance",
  estimate_revision: "estimate",
  customer_contact: "customer_contact",
  // install_review and general_handoff are deliberately NOT mapped — too broad
  // to safely suppress AI signal.
};

/**
 * Classify an AI attention item into a verb bucket from its title/reason.
 * Returns null when classification is ambiguous → no suppression.
 */
function classifyAiVerb(item: AttentionItem): DedupeVerb | null {
  const haystack = `${item.title} ${item.reason ?? ""}`.toLowerCase();
  if (/\b(material|po|purchase order|stage|stock|stag(?:e|ed|ing))\b/.test(haystack)) return "material";
  if (/\b(reschedul|scheduling|schedule|dispatch|appointment time)\b/.test(haystack)) return "schedule";
  if (/\b(permit|ahj|jurisdiction)\b/.test(haystack)) return "permit";
  if (/\b(issue|alignment|discontinued|fix|defect|qa)\b/.test(haystack)) return "service_issue";
  if (/\b(invoice|payment|deposit|balance|ar)\b/.test(haystack)) return "finance";
  if (/\b(estimate|quote|proposal)\b/.test(haystack)) return "estimate";
  if (/\b(call customer|contact customer|outreach)\b/.test(haystack)) return "customer_contact";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mapping: hand-off → MergedAttentionEntry
// ─────────────────────────────────────────────────────────────────────────────

export interface MergeAttentionContext {
  navigate: NavigateFunction;
  jobs: Job[];
  issues: Issue[];
  runners?: HandoffActionRunners;
  /** Optional cap on hand-off entries (default: all visible). */
  maxHandoffs?: number;
}

function handoffToEntry(task: FollowUpTask, ctx: MergeAttentionContext): MergedAttentionEntry {
  const intent = getEffectiveIntent(task);
  const action = resolveHandoffAction(task, {
    navigate: ctx.navigate,
    jobs: ctx.jobs,
    issues: ctx.issues,
    runners: ctx.runners,
  });
  const overdue = new Date(task.dueDate).getTime() < Date.now();
  const severity: "critical" | "warning" | "info" =
    overdue || task.priority === "high" ? "critical" : "warning";

  const secondaryHref = task.jobId
    ? `/jobs/${task.jobId}`
    : task.issueId
      ? `/issues`
      : task.estimateId
        ? `/quotes/${task.estimateId}`
        : task.opportunityId && !task.opportunityId.startsWith("JOB-")
          ? `/lead/${task.opportunityId}`
          : undefined;

  // Phase 7.1: attach linked records so the workspace can render rich context.
  const linkedJob = task.jobId ? ctx.jobs.find((j) => j.id === task.jobId) : undefined;
  const linkedIssue = task.issueId ? ctx.issues.find((i) => i.id === task.issueId) : undefined;

  return {
    id: `handoff-${task.id}`,
    title: task.customerName ? `${task.customerName} — ${INTENT_LABELS[intent]}` : INTENT_LABELS[intent],
    context: task.notes || "Hand-off awaiting your action.",
    severity,
    sourceKind: "handoff",
    sourceLabel: `Hand-off · ${INTENT_LABELS[intent]}`,
    jobId: task.jobId,
    cta: action.label,
    onActivate: () => {
      runAction(action, {
        navigate: ctx.navigate,
        onError: () => {
          if (secondaryHref) ctx.navigate(secondaryHref);
        },
      }).then((handled) => {
        if (!handled && secondaryHref) ctx.navigate(secondaryHref);
      });
    },
    secondaryHref,
    handoffTask: task,
    linkedJob,
    linkedIssue,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Merge + dedupe — public API.
// ─────────────────────────────────────────────────────────────────────────────

export interface MergeAttentionResult {
  /** Hand-off-derived entries (already mapped, ready to render). */
  handoffEntries: MergedAttentionEntry[];
  /** Persisted Action Queue entries (already mapped, ready to render). */
  queueEntries: MergedAttentionEntry[];
  /** AI item ids that should be suppressed because a hand-off OR queue item covers them. */
  suppressedAiIds: Set<string>;
  /** Queue item ids that should be suppressed because a hand-off covers them. */
  suppressedQueueIds: Set<string>;
}

/* ─── Persisted queue → MergedAttentionEntry ─── */

const QUEUE_INTENT_LABELS: Partial<Record<HandoffIntent, string>> = {
  material_confirmation: "Material Confirmation",
  schedule_coordination: "Schedule Coordination",
  permit_followup: "Permit Follow-up",
  service_resolution: "Service Resolution",
  finance_followup: "Finance Follow-up",
  estimate_revision: "Estimate Revision",
  customer_contact: "Customer Contact",
  install_review: "Install Review",
  general_handoff: "Pending Work",
};

function queueItemToEntry(
  item: ActionQueueItem,
  ctx: MergeAttentionContext,
  onCompleteFallback?: (id: string) => void,
): MergedAttentionEntry {
  const action = resolveQueueItemAction(item, {
    navigate: ctx.navigate,
    onComplete: onCompleteFallback,
  });
  const severity: "critical" | "warning" | "info" =
    item.priority === "high" ? "critical" : item.priority === "low" ? "info" : "warning";
  const intentLabel = item.intent ? QUEUE_INTENT_LABELS[item.intent] : undefined;
  const sourceLabel = `Queue · ${intentLabel ?? "Pending Work"}`;

  const secondaryHref = item.jobId
    ? `/jobs/${item.jobId}`
    : item.issueId
      ? `/issues`
      : item.estimateId
        ? `/opportunities/${item.estimateId}`
        : undefined;

  return {
    id: `queue-${item.id}`,
    title: item.title,
    context: item.summary || item.notes || "Pending work in your queue.",
    severity,
    sourceKind: "ai_action", // shares lane visually with AI items but distinct label
    sourceLabel,
    jobId: item.jobId,
    cta: action.label,
    onActivate: () => {
      runAction(action, {
        navigate: ctx.navigate,
        onError: () => {
          if (secondaryHref) ctx.navigate(secondaryHref);
        },
      }).then((handled) => {
        if (!handled && secondaryHref) ctx.navigate(secondaryHref);
      });
    },
    secondaryHref,
    linkedFromHandoffId: item.linkedHandoffId ?? item.originHandoffId,
    linkedJob: item.jobId ? ctx.jobs.find((j) => j.id === item.jobId) : undefined,
    linkedIssue: item.issueId ? ctx.issues.find((i) => i.id === item.issueId) : undefined,
    linkedQueueItem: item,
  };
}

/**
 * Merge hand-offs, persisted Action Queue items, and AI signals into a
 * single coherent attention surface.
 *
 * Precedence (when (jobId, verb) collides):
 *   hand-off > queue item > AI signal
 *
 * Each lane returns its own pre-mapped entries; the consumer concatenates
 * them. Suppression is reported as id sets the consumer can filter against.
 */
export function mergeAttentionFeed(
  handoffs: FollowUpTask[],
  aiItems: AttentionItem[],
  ctx: MergeAttentionContext,
  queueItems: ActionQueueItem[] = [],
  onCompleteQueueItem?: (id: string) => void,
): MergeAttentionResult {
  // Map each hand-off into an entry.
  const cappedHandoffs = ctx.maxHandoffs ? handoffs.slice(0, ctx.maxHandoffs) : handoffs;
  const handoffEntries = cappedHandoffs.map((t) => handoffToEntry(t, ctx));

  // Build (jobId, verb) suppression keys from hand-offs.
  const handoffSuppressionKeys = new Set<string>();
  for (const t of cappedHandoffs) {
    if (!t.jobId) continue;
    const intent = getEffectiveIntent(t);
    const verb = INTENT_TO_VERB[intent];
    if (!verb) continue;
    handoffSuppressionKeys.add(`${t.jobId}::${verb}`);
  }

  // Suppress queue items whose (jobId, intent→verb) matches a hand-off.
  const suppressedQueueIds = new Set<string>();
  const visibleQueueItems: ActionQueueItem[] = [];
  for (const item of queueItems) {
    const verb = item.intent ? INTENT_TO_VERB[item.intent] : undefined;
    if (item.jobId && verb && handoffSuppressionKeys.has(`${item.jobId}::${verb}`)) {
      suppressedQueueIds.add(item.id);
      continue;
    }
    visibleQueueItems.push(item);
  }
  const queueEntries = visibleQueueItems.map((q) =>
    queueItemToEntry(q, ctx, onCompleteQueueItem),
  );

  // Build queue suppression keys (queue items also suppress AI duplicates).
  const queueSuppressionKeys = new Set<string>();
  for (const item of visibleQueueItems) {
    if (!item.jobId || !item.intent) continue;
    const verb = INTENT_TO_VERB[item.intent];
    if (!verb) continue;
    queueSuppressionKeys.add(`${item.jobId}::${verb}`);
  }

  // Find AI items that match either a hand-off OR a queue item (jobId, verb).
  const suppressedAiIds = new Set<string>();
  for (const item of aiItems) {
    if (!item.jobId) continue;
    const verb = classifyAiVerb(item);
    if (!verb) continue;
    const key = `${item.jobId}::${verb}`;
    if (handoffSuppressionKeys.has(key) || queueSuppressionKeys.has(key)) {
      suppressedAiIds.add(item.id);
    }
  }

  return { handoffEntries, queueEntries, suppressedAiIds, suppressedQueueIds };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lineage — walks `originHandoffId` chain. Pure, deterministic.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Walk back through chained hand-offs from `task` to the original root.
 * Returns the chain in oldest-first order. Self-protected against cycles.
 */
export function getWorkflowLineage(
  task: FollowUpTask,
  allTasks: FollowUpTask[],
): FollowUpTask[] {
  const byId = new Map(allTasks.map((t) => [t.id, t]));
  const chain: FollowUpTask[] = [task];
  const seen = new Set<string>([task.id]);

  let cursor: FollowUpTask | undefined = task;
  while (cursor?.originHandoffId) {
    const parent = byId.get(cursor.originHandoffId);
    if (!parent || seen.has(parent.id)) break;
    seen.add(parent.id);
    chain.unshift(parent);
    cursor = parent;
  }
  return chain;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight workflow state — for UI badges. NOT a replacement for
// FollowUpTask.status; this is a derived view that includes "chained".
// ─────────────────────────────────────────────────────────────────────────────

export type WorkflowState = "open" | "chained" | "completed";

export function getWorkflowState(task: FollowUpTask): WorkflowState {
  if (task.status === "completed") {
    return task.completionOutcome === "completed_and_created_followup"
      ? "chained"
      : "completed";
  }
  return "open";
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 5 — Hand-off ↔ Action Queue Convergence
//
// Authority model (deterministic, one-way):
//
//   • Queue item COMPLETED  → linked hand-off auto-resolves (work-of-record).
//   • Hand-off COMPLETED    → queue item is UNCHANGED (routing-of-record only).
//   • Either side DISMISSED → other side is UNCHANGED.
//
// Rationale: the queue item represents the *real downstream work*; the
// hand-off represents the *routing decision*. Completing the work satisfies
// the routing; acknowledging the routing does not satisfy the work.
//
// All sync entry points live HERE so no UI component, context, or execution
// handler can introduce contradictory rules.
// ─────────────────────────────────────────────────────────────────────────────

import { applyCompletionOutcome } from "./handoffActions";

/* ─── Selectors ─── */

export function getLinkedQueueItem(
  task: FollowUpTask,
  items: ActionQueueItem[],
): ActionQueueItem | undefined {
  if (!task.linkedQueueItemId) return undefined;
  return items.find((i) => i.id === task.linkedQueueItemId);
}

export function getLinkedHandoff(
  item: ActionQueueItem,
  tasks: FollowUpTask[],
): FollowUpTask | undefined {
  const id = item.linkedHandoffId ?? item.originHandoffId;
  if (!id) return undefined;
  return tasks.find((t) => t.id === id);
}

/** Connected workflow state for UI badges and audit. */
export type ConnectedWorkflowState =
  | { kind: "standalone" }
  | { kind: "linked-open"; partnerId: string; partnerKind: "queue" | "handoff" }
  | { kind: "linked-completed"; partnerId: string; partnerKind: "queue" | "handoff" };

export function getConnectedWorkflowStateForHandoff(
  task: FollowUpTask,
  items: ActionQueueItem[],
): ConnectedWorkflowState {
  const linked = getLinkedQueueItem(task, items);
  if (!linked) return { kind: "standalone" };
  return linked.status === "completed"
    ? { kind: "linked-completed", partnerId: linked.id, partnerKind: "queue" }
    : { kind: "linked-open", partnerId: linked.id, partnerKind: "queue" };
}

export function getConnectedWorkflowStateForQueueItem(
  item: ActionQueueItem,
  tasks: FollowUpTask[],
): ConnectedWorkflowState {
  const linked = getLinkedHandoff(item, tasks);
  if (!linked) return { kind: "standalone" };
  return linked.status === "completed"
    ? { kind: "linked-completed", partnerId: linked.id, partnerKind: "handoff" }
    : { kind: "linked-open", partnerId: linked.id, partnerKind: "handoff" };
}

/* ─── Sync rule: queue item completion → resolve linked hand-off ─── */

export interface QueueCompletionSyncResult {
  /** The hand-offs that were resolved as a side-effect of the completion. */
  resolvedHandoffIds: string[];
  /** Updated tasks array (immutable; safe to pass straight to a setter). */
  nextTasks: FollowUpTask[];
}

/**
 * Apply the queue→hand-off resolve rule. Pure function: returns the next
 * task list, never mutates the input. Idempotent — safe to call repeatedly.
 *
 * Resolves any open hand-off where:
 *   - the hand-off's `linkedQueueItemId` matches the completed item, OR
 *   - the queue item's `linkedHandoffId` / `originHandoffId` points back.
 */
export function syncOnQueueItemCompleted(
  completedItem: ActionQueueItem,
  tasks: FollowUpTask[],
): QueueCompletionSyncResult {
  const candidateHandoffIds = new Set<string>();
  if (completedItem.linkedHandoffId) candidateHandoffIds.add(completedItem.linkedHandoffId);
  if (completedItem.originHandoffId) candidateHandoffIds.add(completedItem.originHandoffId);

  const resolvedHandoffIds: string[] = [];
  const nextTasks = tasks.map((t) => {
    if (t.status === "completed") return t;
    const matchByLink = t.linkedQueueItemId === completedItem.id;
    const matchByBackref = candidateHandoffIds.has(t.id);
    if (!matchByLink && !matchByBackref) return t;
    resolvedHandoffIds.push(t.id);
    return applyCompletionOutcome(t, "completed");
  });

  return { resolvedHandoffIds, nextTasks };
}

/* ─── Sync rule: hand-off completion → no-op on queue (documented). ─── */

/**
 * Hand-off completion intentionally does NOT advance the linked queue item.
 * This function exists to make the rule explicit at the call-site (so future
 * readers don't add hidden behavior) and to return the `nextItems` unchanged
 * for symmetry with `syncOnQueueItemCompleted`.
 */
export function syncOnHandoffCompleted(
  _completedTask: FollowUpTask,
  items: ActionQueueItem[],
): { nextItems: ActionQueueItem[] } {
  return { nextItems: items };
}
